import React from 'react'
import { Document, Page, Text, View, StyleSheet, Image as PdfImage, Font, Link as PdfLink } from '@react-pdf/renderer';
import { common } from '@mui/material/colors';
import { formatDate, toBase64, compressImage } from '../../../_helpers';
import { useTranslation } from "react-i18next";
import { getProjectLogoForReport } from "../../../apis/surveyingApis";
import { URLS } from '../../../apis/urls';
import { Buffer } from 'buffer';
import { QuestionnaireItemFreeTextSize, QuestionnaireItemType } from '../../../_helpers/enumsHelper';

Font.register({
    family: 'Roboto',
    fonts: [
        { src: "/Content/fonts/Roboto-Regular.ttf", fontWeight: 400 },
        { src: "/Content/fonts/Roboto-Medium.ttf", fontWeight: 500 },
    ]
});

const reportCompletionKeys = [
    { label: "general.projectName", key: "projectName", buildingOnly: false },
    { label: "general.projectNumber", key: "projectNo", buildingOnly: false },
    { label: "survey.report.contractor", key: "contractor", buildingOnly: false },
    { label: "survey.report.transfereeS", key: "responsible", buildingOnly: true },
    { label: "layout.navbar.address", key: "address", buildingOnly: false },
    { label: "general.buildingNumber", key: "buildingNoExtern", buildingOnly: true },
    { label: "survey.report.certificateNumber", key: "guaranteeCertNo", buildingOnly: true },
    { label: "general.date", key: "date", buildingOnly: false },
];


const styles = StyleSheet.create({
    document: {
        backgroundColor: common.white
    },
    topSection: {
        marginTop: 10,
        flexDirection: "row"
    },
    page: {
        display: "flex",
        paddingTop: 20,
        paddingBottom: 20,
        paddingLeft: 60,
        paddingRight: 32,
        flexDirection: "column",
    },
    reportCompletionSection: {
        padding: 1,
        flexDirection: "column",
        width: "50%"
    },
    reportCompletionDetail: {
        marginBottom: 5,
        flexDirection: "row"
    },
    reportCompletionDetailText: {
        fontSize: 9,
        flexGrow: 1,
        fontFamily: "Roboto",
    },
    reportCompletionDetailTextLabel: {
        fontFamily: "Roboto",
        width: 100,
        fontSize: 9,
    },
    generalText: {
        fontSize: 8,
        flexGrow: 1,
        fontFamily: "Roboto",
    },
    answerText: {
        fontSize: 8,
        flexGrow: 1,
        fontFamily: "Roboto",
        marginBottom: 2
    },
    section: {
        padding: 10,
        flexDirection: 'row',
    },
    title: {
        fontFamily: "Roboto",
        fontSize: 11.5,
        fontWeight: 500,
        lineHeight: 1.5,
        letterSpacing: 0.5
    },
    jpds_image: {
        maxHeight: 116,
        width: 200,
    },
    signatureImage: {
        marginTop: 10,
        height: 70,
        width: 200,
        objectFit: "contain",
    },
    questionBlock: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 0,
        padding: 0,
        borderTop: "1px dashed grey",
        paddingTop: 4
    },
    infoContainer: {
        marginTop: 5,
        marginBottom: 5
    },
    freeTextSmall: {
        fontSize: 7,
        fontFamily: "Roboto",
        marginBottom: 4,
        flexGrow: 1,
    },
    freeTextMedium: {
        fontSize: 8.5,
        fontFamily: "Roboto",
        marginBottom: 4,
        flexGrow: 1,
    },
    freeTextLarge: {
        fontSize: 10.5,
        fontFamily: "Roboto",
        marginBottom: 4,
        flexGrow: 1,

    },
    footer: {
        position: "absolute",
        bottom: 5,
        left: 10,
        right: 10,
        margin: 10,
        borderTop: "1px solid black"
    },
    footerContent: {
        marginTop: 5,
        justifyContent: "space-between",
        flexDirection: "row",
    },
    footerPageLabel: {
        fontSize: 9,
        fontFamily: "Roboto",
    },
    infoRow: {
        flexDirection: 'row'
    },
    attachmentsContainer: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
    },
    attachmentView: {
        width: 150,
        height: 110,                  // ⭐ FIXED HEIGHT
        margin: 5,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between", // ⭐ PUSH TEXT TO BOTTOM
        alignItems: "center",
        textAlign: "center"
    },
    thumbnail: {
        width: "100%",
        maxHeight: 100,               // image stays at top
        objectFit: "contain",
        flexGrow: 1,                  // ⭐ image expands to fill space
    },
    fileName: {
        fontSize: 7,
        textAlign: "center",
        width: "100%"
    },
});

const formatStringNewLine = (text) => text.replace(',', '\n');

const infoRow = (key, survey) => {
    switch (key) {
        case "projectName": {
            return survey[key].replace(`${survey.projectNo} - `, "");
        }
        case "date": {
            return formatDate(new Date());
        }
        case "address": {
            if (!survey.buildingId)
                return survey.projectCity;
            return formatStringNewLine(survey[key]);
        }
        case "responsible": {
            return (
                <View style={styles.infoRow}>
                    <Text>{survey.buyerRenter1}{"\n"}</Text>
                    <Text>{survey.buyerRenter2}</Text>
                </View>
            );
        }
        default: {
            return survey[key]
        }
    }
}

const getFileThumbnail = (attachment) => async () => {
    const maxRetries = 10; // Maximum number of retries
    let retries = 0;
    while (retries < maxRetries) {
        try {
            const url = URLS.GET_ATTACHMENT_THUMBNAIL + attachment.fileId;
            const response = await fetch(url);
            if (!response.ok || response.status === 502) {
                retries++;
                console.log(`Error fetching or compressing image (attempt ${retries}):`);
                continue;
            }
            if (response.headers.get('content-type') === "text/html") {
                return "/Content/Images/Background/report-img-placeholder.png"
            }
            try {
                const blob = await response.blob();
                const compressedImage = await compressImage(await toBase64(blob), 500, 500);
                if (compressedImage) {
                    return compressedImage;
                } else {
                    console.log("Compressed image not found");
                    return "/Content/Images/Background/report-img-placeholder.png"
                }
            }
            catch (error) {
                console.log("Error in compressing image", error);
                return "/Content/Images/Background/report-img-placeholder.png"
            }
        } catch (error) {
            retries++;
            console.log(`Error fetching image or compressing image (attempt ${retries}):`, error);
        }
    }
    return "/Content/Images/Background/report-img-placeholder.png"
}

export const usePDFReport = (survey) => {
    const { t } = useTranslation();

    return generatePDFReport({
        t,
        survey
    });
};

export const generatePDFReport = ({
    t,
    survey
}) => {
    const logo = survey ? () => Promise.resolve(getProjectLogoForReport(survey.projectId)
        .then(data => {
            if (data.data && data.data.byteLength > 0) {
                return "data:" + data.type + ";base64, " + Buffer.from(data.data, 'base64').toString('base64');
            }
            return data.data;
        })) : null;

    const title = survey?.reportHeader;

    const getFreeTextStyle = (size) => {
        switch (size) {
            case QuestionnaireItemFreeTextSize.Small:
                return styles.freeTextSmall;
            case QuestionnaireItemFreeTextSize.Medium:
                return styles.freeTextMedium;
            case QuestionnaireItemFreeTextSize.Large:
                return styles.freeTextLarge;
            default:
                return styles.freeTextMedium;
        }
    };
    return (
        <Document
            author="Huisinfo"
            keywords={title}
            subject={title}
            title={title}
        >
            {survey &&
                <Page size="A4" style={styles.page}>
                    <View style={styles.footer} render={({ pageNumber, totalPages }) => (
                        <View style={styles.footerContent}>
                            <Text style={styles.reportCompletionDetailText}>{formatDate(new Date())}</Text>
                            <Text style={styles.footerPageLabel}>{t("survey.report.pagination.label", { pageNumber: pageNumber, totalPages: totalPages })}</Text>
                        </View>
                    )} fixed />

                    <View style={styles.topSection} fixed>
                        <View style={[styles.reportCompletionSection, { flexGrow: 1 }]}>
                            <Text style={styles.title}>{title}</Text>
                            <View>
                                {
                                    reportCompletionKeys.filter(x => !!survey.buildingId || !x.buildingOnly).map(p => {
                                        return (
                                            <View key={p.key} style={styles.reportCompletionDetail}>
                                                <Text style={styles.reportCompletionDetailTextLabel}>
                                                    {t(p.label)} :
                                                </Text>
                                                <Text style={styles.reportCompletionDetailText}>
                                                    {infoRow(p.key, survey)}
                                                </Text>
                                            </View>
                                        )
                                    })
                                }
                            </View>
                        </View>
                        {logo && <View style={styles.section}>
                            <PdfImage style={styles.jpds_image} src={logo} cache={true} />
                        </View>}
                    </View>

                    <View>
                        {survey.sections?.length && survey.sections.map((section, index) => (
                            <View key={section.id} style={styles.infoContainer}>
                                {
                                    section.questions.map((question, qIndex) => {
                                        return (
                                            <View wrap={false} key={question.id}>
                                                {
                                                    qIndex === 0 &&//to keep it inside wrap={false}, it is done like this so that first question always come together with section title
                                                    <>
                                                        <Text style={[styles.title]}>
                                                            {section.title}
                                                        </Text>
                                                        <Text style={[styles.title, { marginBottom: 0, fontSize: 9 }]}>
                                                            {section.description}
                                                        </Text>
                                                    </>
                                                }
                                                <View style={styles.questionBlock}>
                                                    <View style={{
                                                        width: 12,
                                                        height: 3,
                                                        borderRadius: 3,
                                                        backgroundColor: 'black',
                                                        marginTop: 3,
                                                        marginRight: 6,
                                                        marginLeft: 3
                                                    }} />
                                                    <Text style={getFreeTextStyle(question.freeTextSize)}>
                                                        {question.text}
                                                    </Text>
                                                </View>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                                                    <View style={{
                                                        width: 21,
                                                        height: 6,
                                                    }} />
                                                    <View>
                                                        {
                                                            (question.type === QuestionnaireItemType.Radio) &&
                                                            <>
                                                                {
                                                                    question.options?.map((option) =>
                                                                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                                                                            <View
                                                                                style={{
                                                                                    width: 10,
                                                                                    height: 10,
                                                                                    borderWidth: 1,
                                                                                    borderColor: '#000',
                                                                                    borderRadius: 5,
                                                                                    marginRight: 5,
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center'
                                                                                }}
                                                                            >
                                                                                {question.answers?.includes(option?.id) && (
                                                                                    <View
                                                                                        style={{
                                                                                            width: 5,
                                                                                            height: 5,
                                                                                            borderRadius: 3,
                                                                                            backgroundColor: '#000'
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </View>

                                                                            <Text style={styles.generalText}>
                                                                                {option?.option}
                                                                            </Text>
                                                                        </View>
                                                                    )
                                                                }
                                                            </>
                                                        }
                                                        {
                                                            (question.type === QuestionnaireItemType.Checkbox) &&
                                                            <>
                                                                {
                                                                    question.options.map((option) =>
                                                                        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                                                                            <View
                                                                                style={{
                                                                                    width: 10,
                                                                                    height: 10,
                                                                                    borderWidth: 1,
                                                                                    borderColor: '#000',
                                                                                    marginRight: 5,
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center'
                                                                                }}
                                                                            >
                                                                                {question.answers?.includes(option?.id) && (
                                                                                    <View
                                                                                        style={{
                                                                                            width: 5,
                                                                                            height: 5,
                                                                                            backgroundColor: '#000'
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </View>

                                                                            <Text style={styles.generalText}>
                                                                                {option?.option}
                                                                            </Text>
                                                                        </View>
                                                                    )
                                                                }
                                                            </>
                                                        }
                                                        {
                                                            (question.type === QuestionnaireItemType.Dropdown) &&
                                                            <Text style={styles.answerText}>
                                                                {question.options?.filter(x => question.answers.includes(x.id))[0]?.option}
                                                            </Text>
                                                        }
                                                        {
                                                            (question.type === QuestionnaireItemType.ShortText) &&
                                                            <Text style={styles.answerText}>{question.answers.length === 1 && question.answers[0]}</Text>
                                                        }
                                                        {
                                                            (question.type === QuestionnaireItemType.Paragraph) &&
                                                            <Text style={styles.answerText}>{question.answers.length === 1 && question.answers[0]}</Text>
                                                        }
                                                        {
                                                            (question.type === QuestionnaireItemType.Date) &&
                                                            <Text style={styles.answerText}>{question.answers.length === 1 && formatDate(new Date(question.answers[0]))}</Text>
                                                        }
                                                        {
                                                            (question.type === QuestionnaireItemType.Time) &&
                                                            <Text style={styles.answerText}>{question.answers.length === 1 && question.answers[0]}</Text>
                                                        }
                                                        {
                                                            (question.type === QuestionnaireItemType.Signature) &&
                                                            <>
                                                                <PdfImage style={styles.signatureImage} src={`${"data:image/png;base64," + question.signature?.content}`} />
                                                                <Text style={[styles.answerText, { textAlign: "center", width: 200 }]}>{question.answers.length === 1 && question.answers[0]}</Text>
                                                            </>
                                                        }

                                                        <View style={styles.attachmentsContainer}>
                                                            {
                                                                question.attachments.map(attachment => (
                                                                    <PdfLink src={URLS.GET_ATTACHMENT + attachment.fileId}>
                                                                        <View key={attachment.fileId} style={styles.attachmentView}>
                                                                            <PdfImage
                                                                                cache={true}
                                                                                alt="Logo"
                                                                                style={styles.thumbnail}
                                                                                src={getFileThumbnail(attachment)}
                                                                            />
                                                                            <Text style={styles.fileName} numberOfLines={1}>
                                                                                {attachment.fileName}
                                                                            </Text>
                                                                        </View>
                                                                    </PdfLink>
                                                                ))
                                                            }
                                                        </View>
                                                    </View>
                                                </View>
                                            </View>
                                        )
                                    })
                                }
                            </View>
                        ))}

                    </View>
                </Page>
            }
        </Document>
    );
};